import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sessionData } from "../helpers/session-data";
import { chatService } from "../services/chat.service";
import { getApiErrorMessage } from "utils/http/apiError";
import type { RootState } from "../types/store";
import { selectChatSenderIdentity } from "./authSelectors";
import {
  normalizeActiveConversationItem,
  type ActiveConvoApiItem,
  type ChatClientApiItem,
  type GetConversationUsersResponse,
} from "../services/chat.service";
import type { UserApiItem } from "../services/users.service";

export interface ChatState {
  businessTeam: UserApiItem[];
  clients: ChatClientApiItem[];
  conversations: ActiveConvoApiItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatState = {
  businessTeam: [],
  clients: [],
  conversations: [],
  isLoading: false,
  error: null,
};

export type FetchChatDataArg = void | { silent?: boolean };

function isSilentFetchChatArg(arg: unknown): boolean {
  return typeof arg === "object" && arg !== null && (arg as { silent?: boolean }).silent === true;
}

export const fetchChatData = createAsyncThunk(
  "chat/fetchChatData",
  async (_arg: FetchChatDataArg, { rejectWithValue, getState }) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!business_id || !device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const state = getState() as RootState;
    const sender_username = selectChatSenderIdentity(state);

    try {
      const [teamRes, clientsRes, convoRes] = await Promise.all([
        chatService.getBusinessTeam({ business_id, device_token, session_id }),
        chatService.getClientsForChat({
          business_id,
          device_token,
          session_id,
          client_type: "REGISTERED",
        }),
        sender_username
          ? chatService.getConversationUsers({
              business_id,
              device_token,
              session_id,
              sender_username,
            })
          : Promise.resolve({ success: true, data: [] } as GetConversationUsersResponse),
      ]);

      if (teamRes?.success === false) {
        return rejectWithValue(getApiErrorMessage(teamRes.errors, "Failed to load team"));
      }
      if (clientsRes?.success === false) {
        return rejectWithValue(getApiErrorMessage(clientsRes.errors, "Failed to load clients"));
      }
      if (convoRes?.success === false) {
        return rejectWithValue(getApiErrorMessage(convoRes.errors, "Failed to load conversations"));
      }

      const businessTeam = Array.isArray(teamRes?.data?.business_team)
        ? teamRes.data.business_team
        : [];
      const clients = Array.isArray(clientsRes?.data?.clients) ? clientsRes.data.clients : [];
      const conversations = Array.isArray(convoRes?.data)
        ? convoRes.data.map((c) => normalizeActiveConversationItem(c as ActiveConvoApiItem))
        : [];

      return { businessTeam, clients, conversations };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : "Failed to load chat data");
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearChatError: (state) => {
      state.error = null;
    },
    clearChat: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatData.pending, (state, action) => {
        if (!isSilentFetchChatArg(action.meta.arg)) {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchChatData.fulfilled, (state, action) => {
        state.businessTeam = action.payload.businessTeam;
        state.clients = action.payload.clients;
        state.conversations = action.payload.conversations;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchChatData.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to load chat data";
      });
  },
});

export const { clearChatError, clearChat } = chatSlice.actions;

export const selectChatBusinessTeam = (state: RootState): UserApiItem[] =>
  state.chat?.businessTeam ?? [];
export const selectChatClients = (state: RootState): ChatClientApiItem[] =>
  state.chat?.clients ?? [];
export const selectChatConversations = (state: RootState): ActiveConvoApiItem[] =>
  state.chat?.conversations ?? [];
export const selectChatLoading = (state: RootState): boolean => state.chat?.isLoading ?? false;
export const selectChatError = (state: RootState): string | null => state.chat?.error ?? null;

export default chatSlice.reducer;
